import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import { generatePath, Link, useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as AiSparkleIcon } from '@assets/icons/ai-sparkle.svg';
import menu from '@assets/images/noImage.jpg';
import BaseModal from '@components/common/form/BaseModal';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { AppConstants, DEFAULT_TABLE_ITEM_SIZE, DEFAULT_TABLE_ITEM_UNLIMIT } from '@constants';
import apiConfig from '@constants/apiConfig';
import { FieldTypes } from '@constants/formConfig';
import { goodsKinds } from '@constants/masterData';
import useDragDrop from '@hooks/useDragDrop';
import useFetch from '@hooks/useFetch';
import useListModal from '@hooks/useListModal';
import useNotification from '@hooks/useNotification';
import useTranslate from '@hooks/useTranslate';
import { BaseTable, DragDropTableV2 } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import routes from '@routes';
import { render } from '@testing-library/react';
import { setData } from '@utils/localStorage';
import { Button, Flex, Modal, Upload } from 'antd';

import GroupFoodForm from './GroupFoodForm';
import styles from './menu.module.scss';
const message = defineMessages({
    objectName: 'Food Groups',
    uploadMenu: 'AI menu import',
    uploadMenuProcessing: 'Processing menu…',
    uploadMenuSuccess: 'Menu has been submitted for processing',
    uploadMenuError: 'Failed to import menu',
});

const MenuListPage = () => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const { restaurantId } = useParams();
    const {
        data,
        mixinFuncs,
        queryFilter,
        loading,
        pagination,
        detail,
        titleModal,
        executeSave,
        loadingSave,
        form,
        openModal,
        onShowModal,
        onCancelModal,
        setIsChangedFormValues,
        onSave,
    } = useListModal({
        apiConfig: apiConfig.groupFood,
        options: {
            pageSize: DEFAULT_TABLE_ITEM_UNLIMIT,
            objectName: translate.formatMessage(message.objectName),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
        },
    });
    const { sortedData, sortColumn, onDragEnd } = useDragDrop({
        data,
        apiConfig: apiConfig.groupFood.update,
        setTableLoading: () => {},
        indexField: 'rank',
        getList: mixinFuncs.getList,
    });

    const notification = useNotification();
    const { execute: executeUploadMenu } = useFetch(apiConfig.file.uploadMenu);
    const { execute: executeImportMenu } = useFetch(apiConfig.food.importMenu);
    const [ importingMenu, setImportingMenu ] = useState(false);

    const handleUploadMenu = ({ file, onSuccess, onError }) => {
        setImportingMenu(true);
        const fail = (error) => {
            setImportingMenu(false);
            notification({ type: 'error', message: translate.formatMessage(message.uploadMenuError) });
            onError && onError(error || new Error('import-menu failed'));
        };
        executeUploadMenu({
            data: { file },
            onCompleted: (uploadRes) => {
                const filePath = uploadRes?.data?.filePath;
                if (uploadRes?.result !== true || !filePath) {
                    return fail();
                }
                executeImportMenu({
                    data: { filePath },
                    onCompleted: (importRes) => {
                        if (importRes?.result !== true) {
                            return fail();
                        }
                        setImportingMenu(false);
                        notification({ type: 'success', message: translate.formatMessage(message.uploadMenuSuccess) });
                        mixinFuncs.getList();
                        onSuccess && onSuccess();
                    },
                    onError: fail,
                });
            },
            onError: fail,
        });
    };

    const columns = [
        sortColumn,
        {
            title: translate.formatMessage(commonMessage.image),
            dataIndex: 'imagePath',
            render: (record) => {
                return (
                    <img
                        src={record ? AppConstants.contentRootUrl + record : menu}
                        alt=""
                        width="70px"
                        height="50px"
                        style={{ objectFit: 'cover' }}
                    />
                );
            },
            width: 100,
        },
        {
            title: translate.formatMessage(commonMessage.name),
            dataIndex: 'name',
            width: 280,
            render: (record, dataRow) => (
                <Link
                    to={
                        generatePath(routes.menuFoodListPage.path, { restaurantId }) +
                        `?groupFoodId=${dataRow.id}&groupName=${dataRow.name}`
                    }
                >
                    {record}
                </Link>
            ),
        },
        {
            title: translate.formatMessage(commonMessage.description),
            dataIndex: 'description',
        },
        mixinFuncs.renderActionColumn(
            {
                edit: true,
                delete: true,
            },
            { width: '120px' },
        ),
    ];

    const searchFields = [
        {
            key: 'name',
            placeholder: translate.formatMessage(commonMessage.name),
        },
    ];

    return (
        <PageWrapper routes={[ { breadcrumbName: translate.formatMessage(message.objectName) } ]}>
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({ fields: searchFields, initialValues: queryFilter })}
                actionBar={
                    <Flex justify="end" align="center" gap={8}>
                        {/* <Upload
                            accept=".jpg,.jpeg,.png,.webp,.pdf"
                            showUploadList={false}
                            customRequest={handleUploadMenu}
                            disabled={importingMenu}
                        >
                            <Button
                                className={styles.aiImportBtn}
                                icon={importingMenu ? undefined : <AiSparkleIcon />}
                                loading={importingMenu}
                            >
                                <span className={styles.aiImportLabel}>
                                    {translate.formatMessage(
                                        importingMenu ? message.uploadMenuProcessing : message.uploadMenu,
                                    )}
                                </span>
                            </Button>
                        </Upload> */}
                        {mixinFuncs.renderActionBar({
                            objectName: translate.formatMessage(commonMessage.foodGroup),
                        })}
                    </Flex>
                }
                baseTable={
                    <DragDropTableV2
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={sortedData || []}
                        onDragEnd={onDragEnd}
                        pagination={pagination}
                        style={{ cursor: 'pointer' }}
                        loading={loading || loadingSave}
                    />
                }
            />
            {openModal && (
                <BaseModal
                    title={
                        detail
                            ? translate.formatMessage(commonMessage.updateNewFoodGroup)
                            : translate.formatMessage(commonMessage.createNewFoodGroup)
                    }
                    width={1000}
                    isModalVisible={openModal}
                    handleCancel={onCancelModal}
                    isEditing={!!detail}
                    form={form}
                    loading={loadingSave}
                >
                    <GroupFoodForm
                        detail={detail}
                        form={form}
                        onSubmit={onSave}
                        actions={mixinFuncs.renderActions()}
                        setIsChangedFormValues={setIsChangedFormValues}
                    />
                </BaseModal>
            )}
        </PageWrapper>
    );
};

export default MenuListPage;
